import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import BigNumber from 'bignumber.js';
import toast from 'react-hot-toast';
import { useGameState } from '../hooks/useGameState';
import { gameDB } from '../services/db';
import { WalletHeader } from './wallet/WalletHeader';
import { BalanceGrid } from './wallet/BalanceGrid';
import { ConversionInfo } from './wallet/ConversionInfo';
import { TokenConverter } from './wallet/TokenConverter';
import { WalletActions } from './wallet/WalletActions';

interface WalletPanelProps {
  isOpen: boolean;
  onClose: () => void;
  walletAddress: string | null;
}

// Constants
const TOKEN_TO_WEI_RATE = 100;
const MIN_WITHDRAWAL = '0.001';
const GAS_FEE_ESTIMATE = '0.0005';
const NETWORK_FEE = '2';

export function WalletPanel({ isOpen, onClose, walletAddress }: WalletPanelProps) {
  const { gameState, updateGameState } = useGameState(walletAddress);
  const [loading, setLoading] = useState(false);
  const [convertAmount, setConvertAmount] = useState('');
  const [ethPrice, setEthPrice] = useState<number | null>(null);
  const [balance, setBalance] = useState<{
    tokens: number;
    wei: BigNumber;
    eth: string;
    ethCad: number | null;
  }>({
    tokens: gameState.balance || 0,
    wei: new BigNumber(gameState.pendingWei || 0),
    eth: '0',
    ethCad: null
  });

  // Fetch ETH price
  useEffect(() => {
    const fetchEthPrice = async () => {
      try {
        const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=cad');
        if (!response.ok) throw new Error(`Failed to fetch ETH price: ${response.statusText}`);
        const data = await response.json();
        setEthPrice(data.ethereum.cad);
      } catch (error) {
        console.error('Error fetching ETH price:', error);
      }
    };

    if (isOpen) {
      fetchEthPrice();
      const interval = setInterval(fetchEthPrice, 60000);
      return () => clearInterval(interval);
    }
  }, [isOpen]);

  // Update balance calculations
  useEffect(() => {
    const wei = new BigNumber(gameState.pendingWei || 0);
    const eth = parseFloat(ethers.formatEther(wei.toString()));
    const ethCad = ethPrice ? eth * ethPrice : null;

    setBalance({
      tokens: gameState.balance || 0,
      wei,
      eth: eth.toString(),
      ethCad
    });
  }, [gameState.balance, gameState.pendingWei, ethPrice]);

  const handleLoadBalance = async () => {
    if (!walletAddress) return toast.error('No wallet connected');
    setLoading(true);
    
    try {
      const progress = await gameDB.getUserProgress(walletAddress);
      if (progress) {
        updateGameState({
          balance: progress.balance,
          pendingWei: parseInt(progress.pending_wei || '0')
        });
        toast.success('Balance updated');
      }
    } catch (error) {
      console.error('Error loading balance:', error);
      toast.error('Failed to load balance');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTokens = async () => {
    if (!walletAddress) return toast.error('No wallet connected');
    setLoading(true);
    
    try {
      const tokensToAdd = 100000000000000000;
      const newBalance = (gameState.balance || 0) + tokensToAdd;
      
      updateGameState({ balance: newBalance });
      await gameDB.saveUserProgress(walletAddress, { balance: newBalance });
      
      toast.success(`Added ${tokensToAdd.toLocaleString()} tokens`);
    } catch (error) {
      console.error('Error adding tokens:', error);
      toast.error('Failed to add tokens');
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = async () => {
    if (!walletAddress || !convertAmount) return;
    
    const amount = parseInt(convertAmount);
    if (isNaN(amount) || amount <= 0) {
      return toast.error('Please enter a valid amount');
    }
    
    if (amount > balance.tokens) {
      return toast.error('Insufficient balance');
    }

    setLoading(true);
    try {
      const weiAmount = Math.floor(amount / TOKEN_TO_WEI_RATE);
      const newBalance = balance.tokens - amount;
      const newPendingWei = balance.wei.plus(weiAmount);

      updateGameState({
        balance: newBalance,
        pendingWei: parseInt(newPendingWei.toString())
      });

      await gameDB.saveUserProgress(walletAddress, {
        balance: newBalance,
        pending_wei: newPendingWei.toString()
      });

      setConvertAmount('');
      toast.success(`Converted ${amount} tokens to ${weiAmount} WEI`);
    } catch (error) {
      console.error('Error converting:', error);
      toast.error('Failed to convert tokens');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!walletAddress || balance.wei.isZero()) {
      return toast.error('No WEI balance to withdraw');
    }

    const ethBalance = ethers.formatEther(balance.wei.toString());
    if (parseFloat(ethBalance) < parseFloat(MIN_WITHDRAWAL)) {
      return toast.error(`Minimum withdrawal is ${MIN_WITHDRAWAL} ETH`);
    }

    setLoading(true);
    try {
      if (!window.ethereum) {
        throw new Error('MetaMask not installed');
      }

      await window.ethereum.request({ method: 'eth_requestAccounts' });
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      
      const feeData = await provider.getFeeData();
      if (!feeData.maxFeePerGas) {
        throw new Error('Could not estimate gas fees');
      }

      const gasLimit = BigInt(21000);
      const maxFeePerGas = feeData.maxFeePerGas;
      const maxPriorityFeePerGas = feeData.maxPriorityFeePerGas || maxFeePerGas;
      const gasCost = maxFeePerGas * gasLimit;
      const amountToSend = BigInt(balance.wei.toString()) - gasCost;

      if (amountToSend <= 0) {
        throw new Error('Amount too small to cover gas fees');
      }

      const tx = await signer.sendTransaction({
        to: walletAddress,
        value: amountToSend,
        gasLimit,
        maxFeePerGas,
        maxPriorityFeePerGas
      });

      toast.loading('Transaction pending...', { id: 'withdraw' });
      await tx.wait();

      updateGameState({ pendingWei: 0 });
      await gameDB.saveUserProgress(walletAddress, { pending_wei: '0' });

      toast.success(`Withdrew ${ethers.formatEther(amountToSend)} ETH`, {
        id: 'withdraw',
        duration: 5000
      });
    } catch (error: any) {
      console.error('Withdrawal error:', error);
      
      if (error.code === 4001) {
        toast.error('Transaction rejected', { id: 'withdraw' });
      } else if (error.code === -32603) {
        toast.error('Internal MetaMask error', { id: 'withdraw' });
      } else if (error.message?.includes('insufficient funds')) {
        toast.error('Insufficient ETH for gas', { id: 'withdraw' });
      } else {
        toast.error(error.message || 'Failed to withdraw', { id: 'withdraw' });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-lg mx-4 border border-gray-700">
        <WalletHeader
          loading={loading}
          onRefresh={handleLoadBalance}
          onAddTokens={handleAddTokens}
        />

        <div className="p-6 space-y-6">
          <BalanceGrid
            tokens={balance.tokens}
            wei={balance.wei}
            eth={balance.eth}
            ethCad={balance.ethCad}
            ethPrice={ethPrice}
          />

          <ConversionInfo
            tokenToWeiRate={TOKEN_TO_WEI_RATE}
            minWithdrawal={MIN_WITHDRAWAL}
            networkFee={NETWORK_FEE}
            gasFeeEstimate={GAS_FEE_ESTIMATE}
          />

          <TokenConverter
            convertAmount={convertAmount}
            onConvertAmountChange={setConvertAmount}
            onConvert={handleConvert}
            loading={loading}
            tokenToWeiRate={TOKEN_TO_WEI_RATE}
          />

          <WalletActions
            onWithdraw={handleWithdraw}
            onClose={onClose}
            loading={loading}
            wei={balance.wei}
          />
        </div>
      </div>
    </div>
  );
}